import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    TextField,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Divider,
    IconButton,
    InputAdornment,
    Avatar,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    LinearProgress,
    Stepper,
    Step,
    StepLabel,
    MobileStepper,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    Edit as EditIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Email as EmailIcon,
    Person as PersonIcon,
    CalendarToday as CalendarIcon,
    Lock as LockIcon,
    Visibility,
    VisibilityOff,
    Send as SendIcon,
    Key as KeyIcon,
    VerifiedUser as VerifiedUserIcon,
    Security as SecurityIcon,
    ArrowBack as ArrowBackIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from "../context/AuthContext";

const DESIGN = {
    blue: "#1565C0",
    blueBright: "#2979FF",
    accent: "#00E5FF",
    accentSoft: "rgba(0,229,255,0.12)",
    surface: "#ffffff",
    surfaceAlt: "#f4f6fb",
    border: "rgba(21,101,192,0.13)",
    borderHover: "rgba(41,121,255,0.35)",
    text: "#0d1b3e",
    textMid: "#3a4a6b",
    textSoft: "#7a8fb5",
    shadow: "0 4px 24px rgba(10,15,46,0.10)",
    shadowHover: "0 12px 40px rgba(21,101,192,0.18)",
    radius: "16px",
    radiusSm: "10px",
};

const GradientHeader = styled(Paper)(({ theme }) => ({
    // background: "#0a0f2e",
    background: "#0f0f47",

    color: theme.palette.common.white,
    padding: theme.spacing(4),
    borderRadius: DESIGN.radius,
    marginBottom: theme.spacing(4),
    boxShadow: '0 8px 40px rgba(10,15,46,0.18)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: '3px',
        background: `linear-gradient(90deg, ${DESIGN.blue}, ${DESIGN.blueBright}, ${DESIGN.accent})`,
    },
}));

const ProfileCard = styled(Card)(({ theme }) => ({
    borderRadius: DESIGN.radius,
    border: `1px solid ${DESIGN.border}`,
    boxShadow: DESIGN.shadow,
    backgroundColor: DESIGN.surface,
    transition: 'all 0.25s ease',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: DESIGN.shadowHover,
        borderColor: DESIGN.borderHover,
    },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
    width: 110,
    height: 110,
    background: `linear-gradient(135deg, ${DESIGN.blue} 0%, ${DESIGN.blueBright} 100%)`,
    fontSize: '2.75rem',
    fontWeight: 800,
    margin: '0 auto',
    border: `3px solid ${DESIGN.surface}`,
    boxShadow: DESIGN.shadowHover,
}));

const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: DESIGN.radiusSm,
    padding: theme.spacing(1.25, 3),
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.875rem',
    letterSpacing: 0.3,
    transition: 'all 0.25s ease',
}));

const VerificationCodeInput = styled(TextField)(({ theme }) => ({
    width: 50,
    '& input': {
        textAlign: 'center',
        fontSize: '1.5rem',
        fontWeight: 800,
        fontFamily: 'monospace',
    },
    '& .MuiOutlinedInput-root': {
        borderRadius: DESIGN.radiusSm,
        '&:hover fieldset': { borderColor: DESIGN.blue },
        '&.Mui-focused fieldset': { borderColor: DESIGN.blue },
    },
}));

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: DESIGN.radiusSm,
        fontSize: '0.875rem',
        '& fieldset': { borderColor: DESIGN.border },
        '&:hover fieldset': { borderColor: DESIGN.blue },
        '&.Mui-focused fieldset': { borderColor: DESIGN.blue },
    },
    '& .MuiInputLabel-root': { fontSize: '0.875rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: DESIGN.blue },
};

const SectionLabel = ({ children }) => (
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: DESIGN.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>
        {children}
    </Typography>
);

const ProfileScreen = () => {
    const { t } = useTranslation();
    const { auth, refreshAuth } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [userData, setUserData] = useState({
        userName: '',
        email: '',
        role: '',
        // isEmailVerified: auth.isEmailVerified,
        createdAt: ''
    });

    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [message, setMessage] = useState({ open: false, type: '', text: '' });
    const [isEditing, setIsEditing] = useState(false);
    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [verificationStep, setVerificationStep] = useState(0);
    const [emailFormData, setEmailFormData] = useState({
        email: '',
        confirmEmail: ''
    });
    const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
    const [verifying, setVerifying] = useState(false);
    const [resending, setResending] = useState(false);
    const [emailFormErrors, setEmailFormErrors] = useState({});

    const [formData, setFormData] = useState({
        email: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState({});

    const verificationSteps = useMemo(() => [
        {
            title: t('profile.verificationStep1Title'),
            description: t('profile.verificationStep1Description')
        },
        {
            title: t('profile.verificationStep2Title'),
            description: t('profile.verificationStep2Description')
        },
        {
            title: t('profile.verificationStep3Title'),
            description: t('profile.verificationStep3Description')
        }
    ], [t]);

    useEffect(() => {
        fetchUserProfile();
    }, []);

    //    useEffect(() => {
    //   if (!loading && !userData.isEmailVerified && !showVerificationModal) {
    //     setVerificationStep(userData.email ? 1 : 0);
    //     setShowVerificationModal(true);
    //   }
    // }, [loading, userData.isEmailVerified]);


    const fetchUserProfile = useCallback(async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/profile`,
                { withCredentials: true }
            );
            const data = response.data;
            setUserData(data);
            setFormData(prev => ({ ...prev, email: data.email || '' }));
            setEmailFormData({
                email: data.email || '',
                confirmEmail: data.email || ''
            });
        } catch (err) {
            showMessage('error', t('profile.errors.fetchError'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    const showMessage = useCallback((type, text) => {
        setMessage({ open: true, type, text });
    }, []);

    const handleCloseMessage = useCallback(() => {
        setMessage(prev => ({ ...prev, open: false }));
    }, []);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    }, []);

    const handleEmailFormChange = useCallback((e) => {
        const { name, value } = e.target;
        setEmailFormData(prev => ({ ...prev, [name]: value }));
        setEmailFormErrors(prev => ({ ...prev, [name]: '' }));
    }, []);

    const validateEmailForm = useCallback(() => {
        const newErrors = {};
        const emailRegex = /\S+@\S+\.\S+/;

        if (!emailFormData.email) {
            newErrors.email = t('profile.errors.emailRequired');
        } else if (!emailRegex.test(emailFormData.email)) {
            newErrors.email = t('profile.errors.invalidEmail');
        }

        if (emailFormData.email !== emailFormData.confirmEmail) {
            newErrors.confirmEmail = t('profile.errors.emailMismatch');
        }

        setEmailFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [emailFormData, t]);

    const handleSubmitEmail = useCallback(async () => {
        if (!validateEmailForm()) return;

        if (emailFormData.email === userData.email) {
            showMessage('info', t('profile.sameEmail'));
            setVerificationStep(1);
            return;
        }

        setUpdating(true);
        try {
            const endpoint = `${process.env.REACT_APP_API_URL}/api/profile/email`;
            const method = userData.email ? 'put' : 'post';

            const response = await axios({
                method,
                url: endpoint,
                data: { email: emailFormData.email },
                withCredentials: true
            });

            setUserData(prev => ({
                ...prev,
                email: emailFormData.email,
                // isEmailVerified: false
            }));

            setFormData(prev => ({ ...prev, email: emailFormData.email }));

            showMessage('success',
                response.data.message ||
                t(userData.email ? 'profile.emailUpdatedSuccess' : 'profile.emailAddedSuccess')
            );

            // 🔥 NUEVO: Enviar automáticamente el código de verificación
            // try {
            //     const verificationResponse = await axios.post(
            //         `${process.env.REACT_APP_API_URL}/api/send-verification-code`,
            //         {},
            //         { withCredentials: true }
            //     );

            //     showMessage('success', verificationResponse.data.message || t('profile.verificationCodeSent'));

            //     // 🔥 Saltar directamente al paso 3
            setVerificationStep(2);

            // } catch (verificationErr) {
            //     const verificationErrorMsg = verificationErr.response?.data?.error || t('profile.errors.emailError');
            //     showMessage('error', verificationErrorMsg);
            //     // Si falla el envío del código, ir al paso 1
            //     setVerificationStep(1);
            // }

        } catch (err) {
            const errorMsg = err.response?.data?.error || t('profile.errors.updateError');
            showMessage('error', errorMsg);
            setVerificationStep(0);
        } finally {
            setUpdating(false);
        }
    }, [validateEmailForm, emailFormData, userData, t, showMessage]);

    const handleSendVerificationCode = useCallback(async () => {
        if (!userData.email) {
            showMessage('error', t('profile.errors.noEmail'));
            return;
        }

        setResending(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/send-verification-code`,
                {},
                { withCredentials: true }
            );

            showMessage('success', response.data.message || t('profile.verificationCodeSent'));
            setVerificationStep(2);

        } catch (err) {
            const errorMsg = err.response?.data?.error || t('profile.errors.emailError');
            showMessage('error', errorMsg);
        } finally {
            setResending(false);
        }
    }, [userData.email, t, showMessage]);

    const handleVerifyCode = useCallback(async () => {
        const code = verificationCode.join('');
        if (code.length !== 6) {
            showMessage('error', t('profile.errors.invalidCodeLength'));
            return;
        }

        setVerifying(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/verify-email`,
                { code },
                { withCredentials: true }
            );

            await refreshAuth();
            setUserData(prev => ({ ...prev, /* isEmailVerified: true */ }));
            showMessage('success', response.data.message || t('profile.verificationSuccess'));

            setTimeout(() => {
                closeVerificationModal();
            }, 2000);

        } catch (err) {
            const errorMsg = err.response?.data?.error || t('profile.errors.verificationError');
            showMessage('error', errorMsg);
        } finally {
            setVerifying(false);
        }
    }, [verificationCode, t, showMessage, refreshAuth]);

    const handleCodeChange = useCallback((index, value) => {
        if (value.length > 1) return;

        const newCode = [...verificationCode];
        newCode[index] = value;
        setVerificationCode(newCode);

        if (value && index < 5) {
            const nextInput = document.getElementById(`verification-code-${index + 1}`);
            nextInput?.focus();
        }
    }, [verificationCode]);

    const handleKeyDown = useCallback((index, e) => {
        if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
            const prevInput = document.getElementById(`verification-code-${index - 1}`);
            prevInput?.focus();
        }
    }, [verificationCode]);

    const handlePrevStep = useCallback(() => {
        if (verificationStep > 0) {
            setVerificationStep(verificationStep - 1);
        } else {
            closeVerificationModal();
        }
    }, [verificationStep]);

    const closeVerificationModal = useCallback(() => {
        setShowVerificationModal(false);
        setVerificationStep(0);
        setVerificationCode(['', '', '', '', '', '']);
        setEmailFormData({
            email: userData.email || '',
            confirmEmail: userData.email || ''
        });
    }, [userData.email]);

    const handleUpdateEmail = useCallback(async () => {
        if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
            showMessage('error', t('profile.errors.invalidEmail'));
            return;
        }

        if (formData.email === userData.email) {
            showMessage('info', t('profile.sameEmail'));
            setIsEditing(false);
            return;
        }

        setUpdating(true);
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/api/profile/email`,
                { email: formData.email },
                { withCredentials: true }
            );

            setUserData(prev => ({
                ...prev,
                email: formData.email,
                // isEmailVerified: false
            }));

            setEmailFormData({
                email: formData.email,
                confirmEmail: formData.email
            });

            showMessage('success', response.data.message || t('profile.emailUpdatedSuccess'));
            setIsEditing(false);

            setVerificationStep(1);
            setShowVerificationModal(true);

        } catch (err) {
            const errorMsg = err.response?.data?.error || t('profile.errors.updateError');
            showMessage('error', errorMsg);
        } finally {
            setUpdating(false);
        }
    }, [formData.email, userData.email, t, showMessage]);

    const handleChangePassword = useCallback(async (e) => {
        e.preventDefault();

        if (!formData.newPassword || formData.newPassword.length < 6) {
            showMessage('error', t('profile.errors.passwordTooShort'));
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            showMessage('error', t('profile.errors.passwordMismatch'));
            return;
        }

        setUpdating(true);
        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/profile/password`,
                {
                    // currentPassword: formData.currentPassword,
                    newPassword: formData.newPassword
                },
                { withCredentials: true }
            );

            showMessage('success', t('profile.passwordChangedSuccess'));
            setIsEditing(false);

            setFormData(prev => ({
                ...prev,
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            }));

        } catch (err) {
            const errorMsg = err.response?.data?.error || t('profile.errors.updateError');
            showMessage('error', errorMsg);
        } finally {
            setUpdating(false);
        }
    }, [formData, t, showMessage]);

    const handleClickShowPassword = useCallback((field) => {
        setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
    }, []);

    const handleCancelEdit = useCallback(() => {
        setIsEditing(false);
        setFormData({
            email: userData.email || '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setErrors({});
    }, [userData.email]);

    const handleUseCurrentEmail = useCallback(() => {
        if (userData.email) {
            setEmailFormData({
                email: userData.email,
                confirmEmail: userData.email
            });
            setVerificationStep(1);
        }
    }, [userData.email]);

    const handleRequestPasswordReset = useCallback(async () => {
        if (!userData.email) {
            showMessage('error', t('profile.errors.noEmailForReset'));
            return;
        }
        // if (!userData.isEmailVerified) {
        //     showMessage('warning', t('profile.errors.emailNotVerifiedForReset'));
        //     return;
        // }

        setUpdating(true);
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/forgot-password`,
                { email: userData.email },
                { withCredentials: true }
            );

            showMessage('success', t('profile.resetLinkSent'));

        } catch (err) {
            showMessage('info', t('profile.resetLinkSent'));
        } finally {
            setUpdating(false);
        }
    }, [userData.email, /* userData.isEmailVerified, */ t, showMessage]);

    if (loading) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress sx={{ color: DESIGN.blue }} size={52} thickness={3} />
                <Typography sx={{ mt: 3, color: DESIGN.textSoft, fontSize: '0.875rem', letterSpacing: 0.3 }}>
                    {t('profile.loading')}
                </Typography>
            </Box>
        );
    }

    const renderVerificationStep = () => {
        switch (verificationStep) {
            case 0:
                return (
                    <Box>
                        {userData.email && (
                            <Alert
                                severity="info"
                                sx={{
                                    mb: 2.5, borderRadius: DESIGN.radiusSm,
                                    '& .MuiAlert-message': { fontSize: '0.875rem' }
                                }}
                            >
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                    {t('profile.emailAlreadyConfigured')}
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleUseCurrentEmail}
                                        sx={{ borderRadius: DESIGN.radiusSm, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem' }}
                                    >
                                        {t('profile.useCurrentEmail')}
                                    </Button>
                                </Box>
                            </Alert>
                        )}
                        <TextField
                            fullWidth
                            label={t('profile.email')}
                            name="email"
                            value={emailFormData.email}
                            onChange={handleEmailFormChange}
                            required
                            type="email"
                            error={!!emailFormErrors.email}
                            helperText={emailFormErrors.email}
                            sx={{ mb: 2, ...fieldSx }}
                            disabled={updating}
                            placeholder={userData.email || "ejemplo@email.com"}
                        />
                        <TextField
                            fullWidth
                            label={t('profile.confirmEmail')}
                            name="confirmEmail"
                            value={emailFormData.confirmEmail}
                            onChange={handleEmailFormChange}
                            required
                            type="email"
                            error={!!emailFormErrors.confirmEmail}
                            helperText={emailFormErrors.confirmEmail}
                            disabled={updating}
                            sx={fieldSx}
                        />
                        <Alert severity="info" sx={{ mt: 2.5, borderRadius: DESIGN.radiusSm, '& .MuiAlert-message': { fontSize: '0.875rem' } }}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                {t('profile.emailPrivacyNote')}
                            </Typography>
                        </Alert>
                    </Box>
                );

            case 1:
                return (
                    <Box textAlign="center">
                        <Box
                            display="flex" justifyContent="center" mb={2.5}
                            sx={{
                                width: 72, height: 72, borderRadius: '50%',
                                bgcolor: DESIGN.accentSoft,
                                border: `1px solid ${DESIGN.borderHover}`,
                                mx: 'auto',
                                alignItems: 'center',
                            }}
                        >
                            <EmailIcon sx={{ fontSize: 32, color: DESIGN.blue }} />
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: DESIGN.text, mb: 1 }}>
                            {t('profile.verificationCodeWillBeSentTo')}
                        </Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.875rem', fontFamily: 'monospace', color: DESIGN.blue, mb: 2 }}>
                            {userData.email}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => setVerificationStep(0)}
                                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', color: DESIGN.textSoft }}
                            >
                                {t('profile.changeEmail')}
                            </Button>
                        </Box>
                        <Typography sx={{ fontSize: '0.72rem', color: DESIGN.textSoft, letterSpacing: 0.4, display: 'block', mt: 1.5 }}>
                            {t('profile.checkSpamFolder')}
                        </Typography>
                    </Box>
                );

            case 2:
                return (
                    <Box>
                        <Typography sx={{ fontSize: '0.875rem', color: DESIGN.textMid, mb: 1 }}>
                            {t('profile.enterVerificationCodeBelow')}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, my: 3 }}>
                            {verificationCode.map((digit, index) => (
                                <VerificationCodeInput
                                    key={index}
                                    id={`verification-code-${index}`}
                                    value={digit}
                                    onChange={(e) => handleCodeChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    inputProps={{ maxLength: 1 }}
                                    disabled={verifying}
                                />
                            ))}
                        </Box>
                        <Box textAlign="center" mt={2}>
                            <Button
                                variant="text"
                                color="primary"
                                onClick={handleSendVerificationCode}
                                disabled={resending}
                                startIcon={resending && <CircularProgress size={14} />}
                                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.82rem' }}
                            >
                                {resending ? t('profile.resending') : t('profile.resendCode')}
                            </Button>
                        </Box>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/*  {!userData.isEmailVerified && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('profile.verificationRequiredAlert')}
                </Alert>
            )} */}

            <GradientHeader elevation={0}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                    <Box>
                        <Typography
                            variant="h3"
                            sx={{ fontWeight: 800, letterSpacing: -0.5, lineHeight: 1.2, fontSize: { xs: '2rem', md: '2.5rem' }, mb: 0.5 }}
                            gutterBottom
                        >
                            {t('profile.title')}
                        </Typography>
                        <Typography sx={{ opacity: 0.65, fontWeight: 400, fontSize: '0.875rem', letterSpacing: 0.2 }}>
                            {t('profile.subtitle')}
                        </Typography>
                    </Box>
                    {/*  <Box display="flex" gap={2} flexWrap="wrap">
                        {!userData.isEmailVerified && (
                            <ActionButton
                                variant="contained"
                                color="warning"
                                startIcon={<VerifiedUserIcon />}
                                onClick={() => {
                                    setVerificationStep(userData.email ? 1 : 0);
                                    setShowVerificationModal(true);
                                }}
                            >
                                {t('profile.verifyEmailButton')}
                            </ActionButton>
                        )}
                        <ActionButton
                            variant="contained"
                            color="inherit"
                            startIcon={isEditing ? <CancelIcon /> : <EditIcon />}
                            onClick={() => setIsEditing(!isEditing)}
                            disabled={updating}
                            sx={{
                                bgcolor: 'white',
                                color: 'primary.main',
                                '&:hover': {
                                    bgcolor: 'grey.50',
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            {isEditing ? t('profile.cancel') : t('profile.editProfile')}
                        </ActionButton>
                    </Box> */}
                </Box>
            </GradientHeader>

            <Snackbar
                open={message.open}
                autoHideDuration={4000}
                onClose={handleCloseMessage}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    severity={message.type}
                    onClose={handleCloseMessage}
                    sx={{ width: '100%', borderRadius: DESIGN.radiusSm, fontWeight: 500, fontSize: '0.875rem' }}
                >
                    {message.text}
                </Alert>
            </Snackbar>

            <Dialog
                open={showVerificationModal}
                onClose={closeVerificationModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: DESIGN.radius,
                        border: `1px solid ${DESIGN.border}`,
                        boxShadow: '0 24px 64px rgba(10,15,46,0.18)',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""', position: 'absolute', top: 0, left: 0, right: 0,
                            height: '3px',
                            background: `linear-gradient(90deg, ${DESIGN.blue}, ${DESIGN.blueBright}, ${DESIGN.accent})`,
                        },
                    }
                }}
            >
                <DialogTitle sx={{ pb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Box sx={{ width: 36, height: 36, borderRadius: DESIGN.radiusSm, bgcolor: DESIGN.accentSoft, border: `1px solid ${DESIGN.borderHover}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <VerifiedUserIcon sx={{ fontSize: 18, color: DESIGN.blue }} />
                            </Box>
                            <Typography sx={{ fontWeight: 800, fontSize: '1rem', letterSpacing: -0.2, color: DESIGN.text }}>
                                {t('profile.verifyEmailTitle')}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={closeVerificationModal}
                            size="small"
                            sx={{
                                width: 32, height: 32, borderRadius: '8px',
                                border: `1px solid ${DESIGN.border}`,
                                color: DESIGN.textSoft,
                                '&:hover': { borderColor: DESIGN.blue, color: DESIGN.blue, bgcolor: DESIGN.accentSoft },
                                transition: 'all 0.2s',
                            }}
                            aria-label={t('common.close')}
                        >
                            <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                    {!isMobile && (
                        <Stepper activeStep={verificationStep} sx={{ mt: 3 }}>
                            {verificationSteps.map((step, index) => (
                                <Step key={index}>
                                    <StepLabel
                                        sx={{
                                            '& .MuiStepLabel-label': { fontSize: '0.78rem', fontWeight: 600 },
                                            '& .MuiStepIcon-root.Mui-active': { color: DESIGN.blue },
                                            '& .MuiStepIcon-root.Mui-completed': { color: DESIGN.blue },
                                        }}
                                    >
                                        {step.title}
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    )}
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    {isMobile && (
                        <MobileStepper
                            variant="dots"
                            steps={3}
                            position="static"
                            activeStep={verificationStep}
                            sx={{ justifyContent: 'center', mb: 2, bgcolor: 'transparent' }}
                            nextButton={null}
                            backButton={null}
                        />
                    )}

                    {/* <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                        {verificationSteps[verificationStep]?.description}
                    </Typography> */}

                    {renderVerificationStep()}
                </DialogContent>

                <DialogActions sx={{ p: 3, pt: 1, borderTop: `1px solid ${DESIGN.border}` }}>
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <Box>
                            <Button
                                onClick={handlePrevStep}
                                startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                                disabled={verifying || resending || updating}
                                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', color: DESIGN.textMid, borderRadius: DESIGN.radiusSm }}
                            >
                                {verificationStep === 0 ? t('profile.later') : t('profile.back')}
                            </Button>
                        </Box>
                        <Box display="flex" gap={2}>
                            {verificationStep === 0 ? (
                                <ActionButton
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSubmitEmail}
                                    disabled={updating || !emailFormData.email || !emailFormData.confirmEmail}
                                    startIcon={updating && <CircularProgress size={16} color="inherit" />}
                                >
                                    {updating ? t('profile.saving') : t('profile.continue')}
                                </ActionButton>
                            ) : verificationStep === 1 ? (
                                <ActionButton
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSendVerificationCode}
                                    disabled={resending || !userData.email}
                                    startIcon={resending ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
                                >
                                    {resending ? t('profile.sending') : t('profile.sendVerificationCode')}
                                </ActionButton>
                            ) : (
                                <ActionButton
                                    variant="contained"
                                    color="primary"
                                    onClick={handleVerifyCode}
                                    disabled={verifying || verificationCode.join('').length !== 6}
                                    startIcon={verifying && <CircularProgress size={16} color="inherit" />}
                                >
                                    {verifying ? t('profile.verifying') : t('profile.verify')}
                                </ActionButton>
                            )}
                        </Box>
                    </Box>
                </DialogActions>
            </Dialog>

            <Grid container spacing={3}>

                <Grid item xs={12} md={8}>
                    <ProfileCard>
                        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                            {isEditing ? (
                                <Box>
                                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', letterSpacing: -0.2, color: DESIGN.text, mb: 3 }}>
                                        {t('profile.editProfile')}
                                    </Typography>

                                    {/* <Box sx={{ mb: 4 }}>
                                        <Typography variant="h6" fontWeight="medium" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <EmailIcon />
                                            {t('profile.emailSection')}
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            label={t('profile.email')}
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            type="email"
                                            error={!!errors.email}
                                            helperText={errors.email}
                                            sx={{ mb: 2 }}
                                        />
                                        <Box display="flex" justifyContent="flex-end">
                                            <ActionButton
                                                variant="contained"
                                                onClick={handleUpdateEmail}
                                                disabled={updating || !formData.email}
                                                startIcon={updating && <CircularProgress size={20} />}
                                            >
                                                {updating ? t('profile.saving') : t('profile.updateEmail')}
                                            </ActionButton>
                                        </Box>
                                    </Box> */}

                                    {/* <Divider sx={{ my: 4 }} /> */}

                                    <Box>
                                        {/* Header sección contraseña */}
                                        <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
                                            <Box sx={{ width: 32, height: 32, borderRadius: DESIGN.radiusSm, bgcolor: DESIGN.accentSoft, border: `1px solid ${DESIGN.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <LockIcon sx={{ fontSize: 16, color: DESIGN.blue }} />
                                            </Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: DESIGN.textMid, letterSpacing: 0.3 }}>
                                                {t('profile.passwordSection')}
                                            </Typography>
                                        </Box>

                                        <Grid container spacing={2}>
                                            {/*   <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label={t('profile.currentPassword')}
                                                    name="currentPassword"
                                                    type={showPassword.current ? 'text' : 'password'}
                                                    value={formData.currentPassword}
                                                    onChange={handleChange}
                                                    required
                                                    error={!!errors.currentPassword}
                                                    helperText={errors.currentPassword}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => handleClickShowPassword('current')}
                                                                    edge="end"
                                                                >
                                                                    {showPassword.current ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Grid> */}

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label={t('profile.newPassword')}
                                                    name="newPassword"
                                                    type={showPassword.new ? 'text' : 'password'}
                                                    value={formData.newPassword}
                                                    onChange={handleChange}
                                                    required
                                                    error={!!errors.newPassword}
                                                    helperText={errors.newPassword}
                                                    sx={fieldSx}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => handleClickShowPassword('new')}
                                                                    edge="end"
                                                                    size="small"
                                                                    sx={{ color: DESIGN.textSoft, '&:hover': { color: DESIGN.blue } }}
                                                                >
                                                                    {showPassword.new ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label={t('profile.confirmPassword')}
                                                    name="confirmPassword"
                                                    type={showPassword.confirm ? 'text' : 'password'}
                                                    value={formData.confirmPassword}
                                                    onChange={handleChange}
                                                    required
                                                    error={!!errors.confirmPassword}
                                                    helperText={errors.confirmPassword}
                                                    sx={fieldSx}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => handleClickShowPassword('confirm')}
                                                                    edge="end"
                                                                    size="small"
                                                                    sx={{ color: DESIGN.textSoft, '&:hover': { color: DESIGN.blue } }}
                                                                >
                                                                    {showPassword.confirm ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>

                                        <Box display="flex" justifyContent="flex-end" mt={3} gap={1.5}>
                                            <ActionButton
                                                variant="outlined"
                                                onClick={handleCancelEdit}
                                                disabled={updating}
                                                sx={{ borderColor: DESIGN.border, color: DESIGN.textMid, '&:hover': { borderColor: DESIGN.blue, color: DESIGN.blue } }}
                                            >
                                                {t('profile.cancel')}
                                            </ActionButton>
                                            <ActionButton
                                                variant="contained"
                                                color="primary"
                                                onClick={handleChangePassword}
                                                disabled={updating /* || !formData.currentPassword */ || !formData.newPassword}
                                                startIcon={updating && <CircularProgress size={16} color="inherit" />}
                                            >
                                                {updating ? t('profile.saving') : t('profile.changePasswordButton')}
                                            </ActionButton>
                                        </Box>
                                    </Box>
                                </Box>
                            ) : (
                                <Grid container spacing={4} alignItems="center">
                                    {/* Avatar */}
                                    <Grid item xs={12} md={4} display="flex" justifyContent="center">
                                        <StyledAvatar>
                                            {userData.userName?.charAt(0).toUpperCase()}
                                        </StyledAvatar>
                                    </Grid>

                                    {/* Info */}
                                    <Grid item xs={12} md={8}>
                                        <Stack spacing={3}>
                                            {/* Username */}
                                            <Box>
                                                <SectionLabel>{t('profile.username')}</SectionLabel>
                                                <Typography sx={{ fontWeight: 800, fontSize: '1.75rem', letterSpacing: -0.5, color: DESIGN.text, lineHeight: 1.2 }}>
                                                    {userData.userName}
                                                </Typography>
                                            </Box>

                                            {/* Email */}
                                            <Box>
                                                <SectionLabel>{t('profile.email')}</SectionLabel>
                                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                    <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: userData.email ? DESIGN.text : DESIGN.textSoft }}>
                                                        {userData.email || t('profile.noEmail')}
                                                    </Typography>
                                                    {/*  {userData.email && (
                                                        userData.isEmailVerified ? (
                                                            <Chip
                                                                icon={<CheckCircleIcon />}
                                                                label={t('profile.verified')}
                                                                color="success"
                                                                sx={{ fontWeight: 'bold' }}
                                                            />
                                                        ) : (
                                                            <Chip
                                                                label={t('profile.notVerified')}
                                                                color="warning"
                                                                sx={{ fontWeight: 'bold' }}
                                                            />
                                                        )
                                                    )} */}
                                                </Box>
                                            </Box>

                                            {/* Rol */}
                                            <Box>
                                                <SectionLabel>{t('profile.role')}</SectionLabel>
                                                <Chip
                                                    label={userData.role}
                                                    sx={{
                                                        fontWeight: 700, fontSize: '0.82rem',
                                                        bgcolor: DESIGN.accentSoft,
                                                        border: `1px solid ${DESIGN.borderHover}`,
                                                        color: DESIGN.blue,
                                                        borderRadius: DESIGN.radiusSm,
                                                        letterSpacing: 0.3,
                                                    }}
                                                />
                                            </Box>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            )}
                        </CardContent>
                    </ProfileCard>

                    {!isEditing && (
                        <ProfileCard sx={{ mt: 3 }}>
                            <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                                <Typography sx={{ fontWeight: 800, fontSize: '1rem', letterSpacing: -0.2, color: DESIGN.text, mb: 0.5 }}>
                                    {t('profile.quickActions')}
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: DESIGN.textSoft, letterSpacing: 0.3, mb: 3 }}>
                                    {t('profile.quickActionsSubtitle', 'Gestiona tu cuenta y seguridad')}
                                </Typography>

                                <Grid container spacing={2} sx={{ mt: 0 }}>
                                    <Grid item xs={12} md={4}>
                                        <ActionButton
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => setIsEditing(true)}
                                            disabled={updating}
                                        >
                                            {t('profile.editProfile')}
                                        </ActionButton>
                                    </Grid>

                                    {/*  {!userData.isEmailVerified && userData.email && (
                                        <Grid item xs={12} md={4}>
                                            <ActionButton
                                                fullWidth
                                                variant="contained"
                                                color="warning"
                                                startIcon={<VerifiedUserIcon />}
                                                onClick={() => {
                                                    setVerificationStep(1);
                                                    setShowVerificationModal(true);
                                                }}
                                                disabled={updating}
                                            >
                                                {t('profile.verifyEmail')}
                                            </ActionButton>
                                        </Grid>
                                    )} */}

                                    {/*  <Grid item xs={12} md={4}>
                                        <ActionButton
                                            fullWidth
                                            variant="outlined"
                                            color="secondary"
                                            startIcon={<KeyIcon />}
                                            onClick={handleRequestPasswordReset}
                                            disabled={updating || !userData.isEmailVerified}
                                        >
                                            {t('profile.resetPassword')}
                                        </ActionButton>
                                    </Grid> */}
                                </Grid>
                            </CardContent>
                        </ProfileCard>
                    )}
                </Grid>

                <Grid item xs={12} md={4}>
                    <ProfileCard>
                        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                            {/* Header seguridad */}
                            <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                                <Box sx={{ width: 36, height: 36, borderRadius: DESIGN.radiusSm, bgcolor: DESIGN.accentSoft, border: `1px solid ${DESIGN.borderHover}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <SecurityIcon sx={{ fontSize: 18, color: DESIGN.blue }} />
                                </Box>
                                <Typography sx={{ fontWeight: 800, fontSize: '1rem', letterSpacing: -0.2, color: DESIGN.text }}>
                                    {t('profile.securityStatus')}
                                </Typography>
                            </Box>

                            <Stack spacing={3}>
                                {/*   <Box>
                                    <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                                        {t('profile.emailStatus')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                // value={userData.email ? (userData.isEmailVerified ? 100 : 50) : 0}
                                                color={userData.isEmailVerified ? "success" : userData.email ? "warning" : "error"}
                                                sx={{ height: 8, borderRadius: 4 }}
                                            />
                                        </Box>
                                        <Typography variant="body2" fontWeight="bold">
                                            {userData.email
                                                ? (userData.isEmailVerified ? t('profile.verified') : t('profile.pending'))
                                                : t('profile.missing')
                                            }
                                        </Typography>
                                    </Box>
                                </Box>
 */}
                                <Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', letterSpacing: 0.3, color: DESIGN.textMid, mb: 2 }}>
                                        {t('profile.securityTips')}
                                    </Typography>
                                    <Stack spacing={2}>
                                        {/* <Box display="flex" alignItems="flex-start" gap={1}>
                                            <EmailIcon fontSize="small" color={userData.email ? "primary" : "disabled"} />
                                            <Typography variant="body2">
                                                {t('profile.tip1')}
                                            </Typography>
                                        </Box> */}

                                        <Box display="flex" alignItems="flex-start" gap={1.5}>
                                            <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: DESIGN.accentSoft, border: `1px solid ${DESIGN.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.1 }}>
                                                <LockIcon sx={{ fontSize: 14, color: DESIGN.blue }} />
                                            </Box>
                                            <Typography sx={{ fontSize: '0.82rem', color: DESIGN.textMid, lineHeight: 1.6 }}>
                                                {t('profile.tip2')}
                                            </Typography>
                                        </Box>

                                        <Box display="flex" alignItems="flex-start" gap={1.5}>
                                            <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: DESIGN.accentSoft, border: `1px solid ${DESIGN.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.1 }}>
                                                <VerifiedUserIcon sx={{ fontSize: 14, color: DESIGN.blue }} />
                                            </Box>
                                            <Typography sx={{ fontSize: '0.82rem', color: DESIGN.textMid, lineHeight: 1.6 }}>
                                                {t('profile.tip3')}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Stack>

                            {/*        {!userData.email && (
                                <Alert severity="warning" sx={{ mt: 3 }}>
                                    <Typography variant="body2">
                                        {t('profile.addEmailWarning')}
                                    </Typography>
                                </Alert>
                            )} */}

                            {/*  {userData.email && !userData.isEmailVerified && (
                                <Alert severity="info" sx={{ mt: 3 }}>
                                    <Typography variant="body2">
                                        {t('profile.verifyEmailWarning')}
                                    </Typography>
                                </Alert>
                            )} */}
                        </CardContent>
                    </ProfileCard>
                </Grid>
            </Grid>
        </Container>
    );
};

export default ProfileScreen;